<template>
  <NavigationBarLink
    :collapsed-after-animation="collapsedAfterAnimation"
    :collapsed-navigationbar="collapsedNavigationbar"
    :disabled="!canReadAnyProjectSubject"
    :label="project.name"
    :to="{
      name: $can('read', 'Project') ? $routes.ProjectDetails : $routes.ProjectLabeling,
      params: { projectName: project.name },
    }"
  >
    <template #prepend>
      <Transition
        mode="out-in"
        name="icon"
      >
        <div :key="collapsedNavigationbar.toString()">
          <q-img
            class="thumbnail rounded-borders bg-grey-5"
            :class="{
              'q-ml-md': collapsedNavigationbar,
              'q-ml-lg q-my-sm': !collapsedNavigationbar,
              'thumbnail--active': isActiveProject,
            }"
            fit="contain"
            :height="imageSize"
            no-spinner
            no-transition
            placeholder-src="/images/no-image-available.jpg"
            :ratio="1"
            :src="projectThumbnailUrl"
            :width="imageSize"
          />
        </div>
      </Transition>
    </template>

    <template #append>
      <Transition
        mode="out-in"
        name="text"
      >
        <ReadOnlyChip
          v-if="!collapsedNavigationbar"
          :project="project"
        />
      </Transition>
    </template>
  </NavigationBarLink>
</template>

<script lang="ts" setup>
import {
  computed,
  type PropType,
} from 'vue'
import { useRoute } from 'vue-router'
import type { Project } from '~domain/projects/Project'
import { ability } from '~services/ability'
import { Subjects } from '~services/ability/types'
import NavigationBarLink from './NavigationBarLink.vue'
import ReadOnlyChip from './ReadOnlyChip.vue'


const props = defineProps({
  collapsedAfterAnimation: {
    required: true,
    type: Boolean as PropType<boolean>,
  },
  collapsedNavigationbar: {
    required: true,
    type: Boolean as PropType<boolean>,
  },
  project: {
    required: true,
    type: Object as PropType<Project>,
  },
})

const route = useRoute()

const imageSize = computed<string>(
  () => props.collapsedAfterAnimation ? '40px' : '24px',
)

const isActiveProject = computed<boolean>(
  () => route.params.projectName === props.project.name,
)

const canReadAnyProjectSubject = computed(() => {
  const subjects: Subjects[] = ['Annotation', 'Dataset', 'Class', 'Import', 'Project', 'Tag']
  return subjects.some(subject => ability.can('read', subject))
})

const projectThumbnailUrl = computed(() => {
  // Thumbnail loading sometimes takes too much time (5min or more) so we disable them for local (dev) builds
  return process.env.NODE_ENV === 'development' ? '' : `/hub/api/v1/projects/${props.project.id}/thumbnail`
})
</script>

<style lang="sass" scoped>
@import '../css/quasar.variables'

.thumbnail
  border-radius: 8px
  min-width: 24px

  &--active
    border: 1px solid var(--q-primary)
</style>
